import { useState } from "react";
import { Plus, X, GraduationCap, UserCheck, ListChecks } from "lucide-react";

/**
 * V21 Phase 1 — Mobile Floating Action Button for the admin shell.
 *
 * Renders as a 56x56 round button anchored bottom-right (above the bottom
 * nav). Tapping opens a radial menu with 3 quick actions. Hidden on lg+
 * screens where there is plenty of room for dedicated buttons.
 *
 * Props:
 *   onCreateDemoLead  () => void — opens CreateDemoLeadModal
 *   onAddFollowUp     () => void — focuses note input on currently-open lead
 *   onAddTutor        () => void — navigates to /become-a-tutor (or admin tutor
 *                                  add — not yet built, so we link the public form)
 */
export default function AdminMobileFab({ onCreateDemoLead, onAddTutor, onAddFollowUp }) {
    const [open, setOpen] = useState(false);

    const actions = [
        {
            id: "create-demo-lead",
            label: "Create Demo Lead",
            icon: GraduationCap,
            color: "bg-orange-500 text-white",
            onClick: () => { setOpen(false); onCreateDemoLead?.(); },
        },
        {
            id: "add-tutor",
            label: "Add Tutor",
            icon: UserCheck,
            color: "bg-purple-600 text-white",
            onClick: () => { setOpen(false); onAddTutor?.(); },
        },
        {
            id: "add-followup",
            label: "Add Follow-up Note",
            icon: ListChecks,
            color: "bg-emerald-600 text-white",
            onClick: () => { setOpen(false); onAddFollowUp?.(); },
        },
    ];

    return (
        <>
            {open && (
                <button
                    type="button"
                    aria-label="Close menu"
                    onClick={() => setOpen(false)}
                    className="lg:hidden fixed inset-0 z-[55] bg-black/30 backdrop-blur-sm"
                    data-testid="admin-fab-backdrop"
                />
            )}
            <div className="lg:hidden fixed right-4 bottom-20 z-[60] flex flex-col items-end gap-2" data-testid="admin-fab">
                {open && (
                    <div className="flex flex-col items-end gap-2 animate-fade-up">
                        {actions.map((a) => (
                            <button
                                key={a.id}
                                type="button"
                                onClick={a.onClick}
                                data-testid={`admin-fab-${a.id}`}
                                className={`group flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full shadow-lg ${a.color} active:scale-95 transition-transform`}
                            >
                                <a.icon className="w-4 h-4" />
                                <span className="text-sm font-semibold whitespace-nowrap">{a.label}</span>
                            </button>
                        ))}
                    </div>
                )}
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="admin-fab-toggle"
                    aria-expanded={open}
                    aria-label={open ? "Close quick actions" : "Open quick actions"}
                    className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform ${
                        open
                            ? "bg-slate-900 text-white rotate-45"
                            : "bg-gradient-to-br from-orange-500 to-orange-600 text-white active:scale-95"
                    }`}
                >
                    {open ? <X className="w-6 h-6" /> : <Plus className="w-7 h-7" />}
                </button>
            </div>
        </>
    );
}
